import { IonPage } from "@ionic/react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { TitleIcon } from "@/components/atoms/title-icon";
import { AuthContent } from "@/components/molecules/auth/content";
import { AuthHeader } from "@/components/molecules/auth/header";
import { AuthPageHeaderTitle } from "@/components/molecules/auth/page-header-title";
import { AuthSubmitButton } from "@/components/molecules/auth/submit-button";
import { AcceptInviteEmployeeForm } from "@/form/accept-invitation.form";
import { BoomerFormContextProvider } from "@/form/boomer/context";
import { api } from "@/lib/backend";
import { type AcceptEmployeeInviteInput } from "@/lib/backend/api";
import { useExtractedMutation } from "@/lib/backend/utils/hooks";
import { exclamationTriangleIcon } from "@/lib/icons/@heroicons/react/24/outline";
import { LoadingIcon } from "@/lib/icons/loading.icon";
import { queryOptions_validate_invite_token } from "@/lib/query/user/invitation/validate-invite";
import { Routes } from "@/lib/router";
import { history } from "@/lib/router/history";
import { logger } from "@/logger";
import { authClient } from "@/utils/auth";

export function AcceptInvitationToken() {
  const { token } = Routes.AuthAcceptInvite.useParams();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [isAccepted, setIsAccepted] = useState(false);

  function getErrorType(error: Error) {
    if (error.message.includes("EXPIRED_INVITATION_TOKEN")) {
      return "expired";
    } else if (error.message.includes("INVALID_INVITATION_TOKEN")) {
      return "invalid";
    }
    return null;
  }

  const { data, isLoading, error } = useQuery({
    ...queryOptions_validate_invite_token(token!),
    enabled: token !== undefined && !isAccepted,
    retry: false,
  });

  const errorType = useMemo(() => {
    if (error) {
      return getErrorType(error);
    }
    return null;
  }, [error]);

  const acceptInviteMutation = useExtractedMutation({
    mutationKey: ["accept-invite", "create"],
    mutationFn: api.api.acceptInviteEmployee,
    onSuccess: () => {
      toast.success(t("actions:accept-invite.success"));

      // Mark as accepted to prevent validation queries from running during navigation
      setIsAccepted(true);

      /**
       * IMPORTANT: Fix race condition between auth state and navigation
       *
       * PROBLEM: After accepting invitation, backend sets session cookie, but Better Auth's session
       * detection is async. This causes:
       *
       * 1. History.replace() changes URL to /app/projects
       * 2. IsAuthenticated changes, triggering React re-render
       * 3. RequireAuth wrapper redirects to sign-in (auth state not fully propagated)
       * 4. Component still mounted, validation runs with wrong path segments as tokens
       *
       * SOLUTION (matches sign-in flow pattern):
       *
       * 1. Notify auth store immediately (authClient.$store.notify)
       * 2. Invalidate user query to trigger re-fetch
       * 3. Small delay before navigation to allow auth state propagation
       * 4. Use global history for cross-outlet navigation (Global → Tab outlet)
       */

      // Step 1: Immediately notify Better Auth of session change (like signin does)
      authClient.$store.notify("$sessionSignal");

      // Step 2: Invalidate user query to force refresh
      queryClient.invalidateQueries({ queryKey: ["me"] });

      // Step 3: Delay navigation to ensure auth state propagates
      setTimeout(() => {
        history.replace(Routes.Projects());
      }, 100);
    },
    onError: (error) => {
      toast.error(t("actions:accept-invite.error"));
      logger.error(error);
    },
  });

  async function handleSubmit(values: AcceptEmployeeInviteInput) {
    await acceptInviteMutation.mutateAsync(values);
  }

  if (isLoading) {
    return (
      <IonPage>
        <AuthHeader />
        <AuthContent>
          <div className="flex items-center justify-center">
            <LoadingIcon />
          </div>
        </AuthContent>
      </IonPage>
    );
  }

  if (errorType) {
    const titleKey =
      errorType === "expired"
        ? "error-codes:EXPIRED_INVITATION_TOKEN"
        : "error-codes:INVALID_INVITATION_TOKEN";

    return (
      <IonPage>
        <AuthHeader parts={{ backButton: { defaultHref: Routes.AuthSignInPassword() } }} />
        <AuthContent>
          <TitleIcon icon={exclamationTriangleIcon} className="text-warning" />

          <AuthPageHeaderTitle
            parts={{
              title: {
                children: t(
                  errorType === "expired"
                    ? "routes:auth.accept-invitation.expired.title"
                    : "routes:auth.accept-invitation.invalid.title",
                ),
              },
              subtitle: {
                children: t(
                  errorType === "expired"
                    ? "routes:auth.accept-invitation.expired.subtitle"
                    : "routes:auth.accept-invitation.invalid.subtitle",
                ),
              },
            }}
          />

          <div className="flex flex-col gap-6">
            <AuthSubmitButton onClick={() => history.replace(Routes.AuthSignInPassword())}>
              {t("routes:auth.accept-invitation.expired.action.backToLogin")}
            </AuthSubmitButton>
          </div>
        </AuthContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <AuthHeader className="border-b border-gray-200">
        <h1 className="text-center text-xl font-semibold text-gray-900">
          {t("custom-auth:invitation.complete-profile")}
        </h1>
      </AuthHeader>
      <AuthContent>
        <BoomerFormContextProvider>
          <AcceptInviteEmployeeForm onSubmit={handleSubmit} disabled={false} invitation={data} />
        </BoomerFormContextProvider>
      </AuthContent>
    </IonPage>
  );
}
